import { Alert, AlertTitle, cn } from '@portals-platform/ui';
import { AlertCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { type FieldErrors, useFormContext } from 'react-hook-form';

export interface FormErrorSummaryProps {
  title?: string;
  /** Map field paths to human labels; falls back to the raw path. */
  labels?: Record<string, string>;
  className?: string;
}

interface FlatError {
  path: string;
  message: string;
}

/** Depth-first walk so nested paths (`owners.0.fullName`) appear, not just top-level keys. */
function flatten(errors: FieldErrors, prefix = ''): FlatError[] {
  const out: FlatError[] = [];
  for (const [key, value] of Object.entries(errors)) {
    if (!value) continue;
    // `root` holds form-level errors (typically a server message). They have no control to
    // link to, so the generated button would scroll nowhere — surface them separately.
    if (!prefix && key === 'root') continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
      out.push({ path, message: value.message });
    } else if (typeof value === 'object') {
      out.push(...flatten(value as FieldErrors, path));
    }
  }
  return out;
}

/**
 * Error roll-up for long forms, with a link to each offending field.
 *
 * On a multi-section form the failing field is often scrolled off-screen, so submitting
 * appears to do nothing. Rendering this above the actions gives the user somewhere to look
 * and something to click.
 */
export function FormErrorSummary({
  title = 'Please fix the following before continuing',
  labels,
  className,
}: FormErrorSummaryProps) {
  const { formState } = useFormContext();
  const ref = useRef<HTMLDivElement>(null);
  const errors = flatten(formState.errors);
  const count = errors.length;

  // Move focus here after a failed submit so keyboard and screen-reader users land on the
  // explanation instead of staying at the button that seemingly did nothing.
  useEffect(() => {
    if (count > 0 && formState.submitCount > 0) ref.current?.focus();
  }, [count, formState.submitCount]);

  if (count === 0 || formState.submitCount === 0) return null;

  return (
    <Alert
      ref={ref}
      variant="destructive"
      tabIndex={-1}
      role="alert"
      aria-live="assertive"
      className={cn('outline-none', className)}
    >
      <AlertCircle aria-hidden="true" />
      <AlertTitle>{title}</AlertTitle>
      <ul className="mt-2 flex list-disc flex-col gap-1 pl-4 text-[12.5px]">
        {errors.map((error) => (
          <li key={error.path}>
            <button
              type="button"
              className="underline underline-offset-2 hover:no-underline"
              onClick={() => {
                const el = document.querySelector<HTMLElement>(`[name="${error.path}"]`);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el?.focus({ preventScroll: true });
              }}
            >
              {labels?.[error.path] ?? error.path}
            </button>
            : {error.message}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
